<script setup lang="ts">
import { computed, toRefs } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    options: ['pulse', 'glow', 'wiggle'],
    required: true,
  },
  radiusClass: {
    type: String,
    options: ['rounded-10', 'rounded-none', 'rounded-sm', 'rounded', 'rounded-md', 'rounded-lg', 'rounded-xl', 'rounded-2xl', 'rounded-3xl'],
    required: false,
  },
  looped: {
    type: Boolean,
    required: false,
  },
});
const { variant, radiusClass, looped } = toRefs(props);
const getClass = computed(() => [
  'highlight',
  `highlight-${variant.value}`,
  `${radiusClass?.value}`,
  { 'rounded-10': !radiusClass?.value },
  { infinite: looped?.value },
]);
</script>

<template>
  <div :class="getClass">
    <slot />
  </div>
</template>

<style scoped>
.highlight {
  @apply relative z-10;
}
.highlight:hover,
.highlight:hover:active {
  animation-play-state: paused !important;
}
.highlight.highlight-glow {
  @apply ring ring-blue-500/30;
}

/* Pulse effect */
.highlight-pulse {
  animation: pulse-animation 1.5s 1;
}
.infinite,
.infinite::after {
  animation-iteration-count: infinite !important;
}
@keyframes pulse-animation {
  0% {
    box-shadow: 0 0 0 0px rgba(0, 0, 0, 0.3);
  }
  100% {
    box-shadow: 0 0 0 12px rgba(0, 0, 0, 0);
  }
}

/* Glow effect */
.highlight-glow::after {
  position: absolute;
  content: '';
  top: 10px;
  left: 0;
  right: 0;
  z-index: -1;
  height: 100%;
  width: 100%;
  transform: scale(0.9) translateZ(0);
  filter: blur(15px);
  /* background: linear-gradient(to left, #ff5770, #e4428d, #c42da8, #9e16c3, #6501de, #9e16c3, #c42da8, #e4428d, #ff5770); */
  background: linear-gradient(to left, #ff2a67, #bf3e8d, #8052b3, #4066d9, #007aff, #4066d9, #8052b3, #bf3e8d, #ff2a67);
  background-size: 200% 200%;
  animation: animateGlow 1.25s linear 1;
}
@keyframes animateGlow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

/* Wiggle effect */
.highlight-wiggle {
  animation: wiggle 2.7s 1;
}
@keyframes wiggle {
  2% {
    -webkit-transform: translateX(3px) rotate(2deg);
    transform: translateX(3px) rotate(2deg);
  }
  4% {
    -webkit-transform: translateX(-3px) rotate(-2deg);
    transform: translateX(-3px) rotate(-2deg);
  }
  6% {
    -webkit-transform: translateX(3px) rotate(2deg);
    transform: translateX(3px) rotate(2deg);
  }
  8% {
    -webkit-transform: translateX(-3px) rotate(-2deg);
    transform: translateX(-3px) rotate(-2deg);
  }
  10% {
    -webkit-transform: translateX(2px) rotate(1deg);
    transform: translateX(2px) rotate(1deg);
  }
  12% {
    -webkit-transform: translateX(-2px) rotate(-1deg);
    transform: translateX(-2px) rotate(-1deg);
  }
  14% {
    -webkit-transform: translateX(2px) rotate(1deg);
    transform: translateX(2px) rotate(1deg);
  }
  16% {
    -webkit-transform: translateX(-2px) rotate(-1deg);
    transform: translateX(-2px) rotate(-1deg);
  }
  18% {
    -webkit-transform: translateX(1px) rotate(0);
    transform: translateX(1px) rotate(0);
  }
  20% {
    -webkit-transform: translateX(-1px) rotate(0);
    transform: translateX(-1px) rotate(0);
  }
}
</style>
